import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/api';

interface SubMenuStats {
  qa: {
    totalApplications: number;
    completedAssessments: number;
    inProgressAssessments: number;
    notStartedAssessments: number;
    completionPercentage: number;
  };
  scoring: {
    totalApplications: number;
    scoredApplications: number;
    avgScore: number;
    distribution: {
      retire: number;
      retain: number;
      rePlatform: number;
      reDesign: number;
    };
  };
  finalDisposition: {
    totalApplications: number;
    dispositionSet: number;
    transferredToIntegration: number;
    pendingDisposition: number;
  };
  reports: {
    availableReports: number;
  };
}

const ApplicationEvaluation: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SubMenuStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load Q&A and Scoring stats from scoring-portfolio endpoint
      const qaResponse = await authFetch('/api/assessments/scoring-portfolio');
      let qaStats = { totalApplications: 0, completedAssessments: 0, inProgressAssessments: 0, notStartedAssessments: 0, completionPercentage: 0 };
      let scoringStats = {
        totalApplications: 0,
        scoredApplications: 0,
        avgScore: 0,
        distribution: { retire: 0, retain: 0, rePlatform: 0, reDesign: 0 }
      };

      if (qaResponse.ok) {
        const qaData = await qaResponse.json();
        const apps = qaData.applications || [];

        // Q&A Stats
        qaStats = {
          totalApplications: apps.length,
          completedAssessments: apps.filter((a: any) => a.assessment_status === 'Completed').length,
          inProgressAssessments: apps.filter((a: any) => a.assessment_status === 'In-Process').length,
          notStartedAssessments: apps.filter((a: any) => a.assessment_status === 'Not Started').length,
          completionPercentage: apps.length > 0
            ? Math.round(apps.filter((a: any) => a.assessment_status === 'Completed').length / apps.length * 100)
            : 0
        };

        // Scoring Stats (from same data)
        const scoredApps = apps.filter((a: any) => a.overall_score !== null && a.overall_score > 0);
        const totalScore = scoredApps.reduce((sum: number, a: any) => sum + (a.overall_score || 0), 0);

        scoringStats = {
          totalApplications: apps.length,
          scoredApplications: scoredApps.length,
          avgScore: scoredApps.length > 0 ? Math.round((totalScore / scoredApps.length) * 100) / 100 : 0,
          distribution: {
            retire: apps.filter((a: any) => a.recommendation === 'Retire').length,
            retain: apps.filter((a: any) => a.recommendation === 'Retain').length,
            rePlatform: apps.filter((a: any) => a.recommendation === 'Re-Platform').length,
            reDesign: apps.filter((a: any) => a.recommendation === 'Re-Design').length,
          }
        };
      }

      // Load final disposition stats from server-calculated statistics
      let finalDispositionStats = { totalApplications: 0, dispositionSet: 0, transferredToIntegration: 0, pendingDisposition: 0 };
      try {
        const fdResponse = await authFetch('/api/final-disposition');
        if (fdResponse.ok) {
          const fdData = await fdResponse.json();
          if (fdData.stats) {
            // Use server-provided stats (covers all records, not just the paginated page)
            finalDispositionStats = {
              totalApplications: fdData.stats.totalApplications || 0,
              dispositionSet: fdData.stats.dispositionSet || 0,
              transferredToIntegration: fdData.stats.transferredToIntegration || 0,
              pendingDisposition: fdData.stats.pendingDispositions || 0,
            };
          } else {
            // Fallback for older API format
            const dispositions = fdData.data || fdData || [];
            finalDispositionStats = {
              totalApplications: dispositions.length,
              dispositionSet: dispositions.filter((d: any) => d.final_4r_model).length,
              transferredToIntegration: dispositions.filter((d: any) => d.transferred_to_integration).length,
              pendingDisposition: dispositions.filter((d: any) => !d.final_4r_model).length,
            };
          }
        }
      } catch (e) {
        console.warn('Could not load final disposition stats');
      }

      setStats({
        qa: qaStats,
        scoring: scoringStats,
        finalDisposition: finalDispositionStats,
        reports: { availableReports: 4 } // Static for now
      });
    } catch (err: any) {
      console.error('Error loading evaluation stats:', err);
      setError('Failed to load evaluation statistics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num);

  if (loading) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Application Evaluation</h1>
              <p className="page-header__subtitle">Loading evaluation data...</p>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div style={{ textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <p>Loading statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header">
          <div className="page-header__content">
            <div className="page-header__title-section">
              <h1 className="page-header__title">Application Evaluation</h1>
            </div>
          </div>
        </div>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#dc2626' }}>{error}</p>
          <button className="btn btn-primary" onClick={loadStats}>Retry</button>
        </div>
      </div>
    );
  }

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: '1px solid #e0e0e0',
  };

  const cardHoverStyle = {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  };

  const statBoxStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    minWidth: '80px',
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Application Evaluation</h1>
            <p className="page-header__subtitle">
              Comprehensive application assessment through questionnaires, scoring, and disposition analysis
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-secondary"
              onClick={loadStats}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span>🔄</span> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>

        {/* Q&A Card */}
        <div
          style={cardStyle}
          onClick={() => navigate('/evaluation/qa')}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' })}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '40px' }}>❓</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1976d2' }}>Application Portfolio Q&A</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>Answer questionnaires for applications</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1976d2' }}>{formatNumber(stats?.qa.totalApplications || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Apps</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{formatNumber(stats?.qa.completedAssessments || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Completed</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>{formatNumber(stats?.qa.inProgressAssessments || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>In Progress</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>{stats?.qa.completionPercentage || 0}%</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Complete</div>
            </div>
          </div>
        </div>

        {/* Scoring Card */}
        <div
          style={cardStyle}
          onClick={() => navigate('/evaluation/scoring')}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' })}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '40px' }}>📊</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1976d2' }}>Application Portfolio Scoring</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>View scores and 4R recommendations</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1976d2' }}>{formatNumber(stats?.scoring.scoredApplications || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Scored</div>
            </div>
            <div style={{ ...statBoxStyle, backgroundColor: '#fee2e2' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#dc2626' }}>{stats?.scoring.distribution.retire || 0}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Retire</div>
            </div>
            <div style={{ ...statBoxStyle, backgroundColor: '#d1fae5' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>{stats?.scoring.distribution.retain || 0}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Retain</div>
            </div>
            <div style={{ ...statBoxStyle, backgroundColor: '#fef3c7' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>{stats?.scoring.distribution.rePlatform || 0}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Re-Platform</div>
            </div>
            <div style={{ ...statBoxStyle, backgroundColor: '#dbeafe' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>{stats?.scoring.distribution.reDesign || 0}</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Re-Design</div>
            </div>
          </div>
        </div>

        {/* Final Disposition Card */}
        <div
          style={cardStyle}
          onClick={() => navigate('/evaluation/final-disposition')}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' })}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '40px' }}>🎯</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1976d2' }}>Final Disposition</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>Set final 4R decisions and transfer to integration</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1976d2' }}>{formatNumber(stats?.finalDisposition.totalApplications || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Apps</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>{formatNumber(stats?.finalDisposition.dispositionSet || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Disposition Set</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#06b6d4' }}>{formatNumber(stats?.finalDisposition.transferredToIntegration || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Transferred</div>
            </div>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>{formatNumber(stats?.finalDisposition.pendingDisposition || 0)}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Pending</div>
            </div>
          </div>
        </div>

        {/* Data Export & Reports Card */}
        <div
          style={cardStyle}
          onClick={() => navigate('/evaluation/reports')}
          onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHoverStyle)}
          onMouseLeave={(e) => Object.assign(e.currentTarget.style, { transform: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' })}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ fontSize: '40px' }}>📈</div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: '#1976d2' }}>Data Export & Reports</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666' }}>Generate and download evaluation reports</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={statBoxStyle}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>{stats?.reports.availableReports || 4}</div>
              <div style={{ fontSize: '12px', color: '#666' }}>Report Types</div>
            </div>
            <div style={{ ...statBoxStyle, flex: 1, minWidth: '150px' }}>
              <div style={{ fontSize: '14px', color: '#666', textAlign: 'center' }}>
                Export Q&A, Scoring, Disposition data to Excel/CSV
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ApplicationEvaluation;
